import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Card from './Card';

/**
 * A single headline metric tile for the admin dashboard. Shows a big number,
 * a label, and an optional sub-line (e.g. "of 1,204 total" or a 24h delta).
 *
 * Kept presentational — the dashboard page owns data fetching and passes
 * already-formatted values. `tone` tints the number for at-a-glance status
 * (e.g. `warn` for the unreviewed-question count, `danger` for refunds).
 *
 * Pass `to` to make the tile a drill-down: the whole card becomes a link to
 * the page that explains the number. A metric an operator cannot open is a
 * dead end — "17 online" raises exactly one question ("who?") and the answer
 * should be one click away, not a manual trip to the Users page with the
 * right filter guessed by hand.
 */

export type MetricTone = 'default' | 'positive' | 'warn' | 'danger' | 'accent';

export interface MetricCardProps {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: MetricTone;
  testId?: string;
  /** Router path (may include a query string) this metric drills into. */
  to?: string;
  /**
   * Accessible name for the link, e.g. "View the 17 online players". Required
   * with `to` — the card's visible text is a label and a bare number, which
   * reads as "Online players 17" with no indication it is actionable.
   */
  linkLabel?: string;
}

const VALUE_TONE: Record<MetricTone, string> = {
  default: 'text-slate-900',
  positive: 'text-emerald-700',
  warn: 'text-amber-700',
  danger: 'text-red-700',
  accent: 'text-indigo-700',
};

export default function MetricCard({
  label,
  value,
  sub,
  tone = 'default',
  testId,
  to,
  linkLabel,
}: MetricCardProps) {
  const body = (
    <>
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <span
        className={`text-2xl font-semibold tabular-nums ${VALUE_TONE[tone]}`}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value}
      </span>
      {sub != null && <span className="text-xs text-slate-500">{sub}</span>}
    </>
  );

  if (!to) {
    return (
      <Card padding="md" data-testid={testId} className="flex flex-col gap-1">
        {body}
      </Card>
    );
  }

  return (
    <Card
      padding="none"
      data-testid={testId}
      // `overflow-hidden` keeps the link's focus ring inside the card's radius;
      // `transition` + hover border are the only affordance that the tile is
      // actionable, since the content itself is identical to a static card.
      className="overflow-hidden transition hover:border-indigo-300 hover:shadow-sm focus-within:border-indigo-400"
    >
      <Link
        to={to}
        aria-label={linkLabel ?? `View details for ${label}`}
        data-testid={testId ? `${testId}-link` : undefined}
        className="flex h-full flex-col gap-1 p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset"
      >
        {body}
        <span
          aria-hidden="true"
          className="mt-auto pt-1 text-xs font-medium text-indigo-600"
        >
          View details →
        </span>
      </Link>
    </Card>
  );
}
